//= require personalised-config.js
//= require personalised-utils.js
//= require personalised-bundle-ui.js
//= require personalised-bundle-utils.js
//= require personalised-validation.js
// CUSTOM: personalised-event-handlers.js

(function() {
  // Ensure namespace exists
  window.personalised = window.personalised || {};
  
  // Module implementation
  const eventHandlersModule = (function() {
    // Private module state to track active event listeners and elements
    const trackedListeners = {
      frequency: new Map(), // Map DOM elements to their handler references
      removeButtons: new Map(),
      variantSelects: new Map(),
      syncListeners: [], // Array of listener objects with event and handler properties
      submissionListeners: [], // Array of listener objects for bundle submission result events
      modalListeners: new Map(),
      performanceListeners: new Map()
    };
    
    /**
     * Handle frequency change events
     * @param {Event} event - The change event
     */
    function handleFrequencyChange(event) {
      const radio = event.target;
      if (!radio.checked) return;
      
      // Provide immediate visual feedback
      window.personalised.bundleUI.setFrequencyActionFeedback(radio);
      
      // Update frequency using bundle API - ensure string value is passed
      const frequencyValue = String(radio.value);
      window.personalised.bundle.setFrequency(frequencyValue);
      // PR-2.4: mark state modified so the review-page timestamp skip does not suppress
      // a defensive sync if this frequency-change sync fails before completing.
      window.personalised.storageManager?.markBundleModified();
      
      // Trigger direct UI updates to ensure UI reflects the frequency change
      window.personalised.bundleUI.renderBundleItems();
      
      // Force price display update with new frequency
      window.personalised.bundleUI.updatePriceDisplay();
      
      // Schedule debounced cart sync - skip in EDIT mode
      try {
        // Skip cart sync in edit mode
        if (window.personalised.utils && window.personalised.utils.getMode() === window.personalised.config.modes.EDIT) return;
        
        if (window.personalised.utils && typeof window.personalised.utils.debouncedRefreshCartFromState === 'function') {
          window.personalised.utils.debouncedRefreshCartFromState('✅ Frequency updated');
        }
        if (window.__isTapcart === true) {
          try { localStorage.setItem('pack_dirty', String(Date.now())); } catch (e) {}
        }
      } catch (err) {
        console.error('Error scheduling frequency update:', err);
      }
    }
    
    /**
     * Handle remove item button clicks
     * @param {Event} event - The click event
     */
    function handleRemoveItem(event) {
      const button = event.currentTarget;
      const rawVariantId = button.getAttribute(window.personalised.config.dataAttributes.removeFromPack);
      const variantId = window.personalised.utils.normalizeShopifyId(rawVariantId);
      
      console.debug('[handleRemoveItem] Removing item', {
        rawVariantId,
        normalizedVariantId: variantId
      });
      
      // Provide immediate visual feedback
      window.personalised.bundleUI.setRemoveButtonActionFeedback(button);
      
      try {
        // Check if this is the last item in the bundle
        const items = window.personalised.bundle.getItems();
        
        if (items.length === 1) {
          // Show confirmation modal instead of removing immediately
          window.personalised.bundleUI.showLastItemConfirmationModal(variantId);
          return; // Exit early - removal will happen in modal handler if confirmed
        }
        
        // Not the last item, proceed with normal removal
        window.personalised.bundle.removeItemByVariantId(variantId);
        // PR-2.4: mark state modified so the review-page timestamp skip does not suppress
        // a defensive sync if this remove sync fails before completing.
        window.personalised.storageManager?.markBundleModified();
        
        // Update UI immediately
        window.personalised.bundleUI.renderBundleItems();
        
        // Schedule debounced cart sync
        const message = window.personalised.bundle.hasItems()
          ? '✅ Item removed from your personalised pack'
          : '✅ Items removed from your personalised pack';
        
        // Skip cart sync in edit mode
        if (window.personalised.utils && window.personalised.utils.getMode() === window.personalised.config.modes.EDIT) return;
        
        if (window.personalised.utils && typeof window.personalised.utils.debouncedRefreshCartFromState === 'function') {
          window.personalised.utils.debouncedRefreshCartFromState(message);
        }
        if (window.__isTapcart === true) {
          try { localStorage.setItem('pack_dirty', String(Date.now())); } catch (e) {}
        }
      } catch (err) {
        window.personalised.bundleUI.showBundleErrorModal();
        console.error('Error removing item from bundle:', err);
      }
    }
    
    /**
     * Handle variant selection changes
     * @param {Event} event - The change event
     */
    function handleVariantChange(event) {
      const select = event.currentTarget;
      try {
        const rawNewVariantId = select.value;
        const rawProductId = select.getAttribute(window.personalised.config.dataAttributes.variantSelect);
        
        // Normalize IDs
        const newVariantId = window.personalised.utils.normalizeShopifyId(rawNewVariantId);
        const productId = window.personalised.utils.normalizeShopifyId(rawProductId);
        
        console.debug('[handleVariantChange] Changing variant', {
          rawProductId,
          normalizedProductId: productId,
          rawNewVariantId,
          normalizedNewVariantId: newVariantId
        });
        
        // Validate bundle state before accessing
        if (!window.personalised.validation.validateBundleLoaded()) {
          console.warn('Bundle not loaded, cannot change variant');
          return;
        }
        
        // Get current items
        const items = window.personalised.bundle.getItems();
        const bundleItem = items.find(item => 
          window.personalised.utils.shopifyIdsEqual(item.productId, productId)
        );
        
        if (bundleItem) {
          // Update variant in-place to preserve item position in the list
          window.personalised.bundle.updateItemVariant(
            bundleItem.productId,
            newVariantId,
            bundleItem.sellingPlanId
          );
          
          // Update UI immediately
          window.personalised.bundleUI.renderBundleItems();
          
          // Schedule debounced cart sync - skip in EDIT mode
          if (window.personalised.utils && window.personalised.utils.getMode() === window.personalised.config.modes.EDIT) return;
          
          if (window.personalised.utils && typeof window.personalised.utils.debouncedRefreshCartFromState === 'function') {
            window.personalised.utils.debouncedRefreshCartFromState('✅ Variant updated in your personalised pack');
          }
          if (window.__isTapcart === true) {
            try { localStorage.setItem('pack_dirty', String(Date.now())); } catch (e) {}
          }
        }
      } catch (err) {
        window.personalised.bundleUI.showBundleErrorModal();
        console.error('Error updating bundle variant:', err);
      }
    }

    /**
     * Clean up frequency change event listeners to prevent memory leaks
     */
    function cleanupFrequencyListeners() {
      trackedListeners.frequency.forEach((handler, element) => {
        element.removeEventListener('change', handler);
      });
      trackedListeners.frequency.clear();
    }
    
    /**
     * Clean up remove button event listeners to prevent memory leaks
     */
    function cleanupRemoveButtonListeners() {
      trackedListeners.removeButtons.forEach((handler, element) => {
        element.removeEventListener('click', handler);
      });
      trackedListeners.removeButtons.clear();
    }
    
    /**
     * Clean up variant select event listeners to prevent memory leaks
     */
    function cleanupVariantSelectListeners() {
      trackedListeners.variantSelects.forEach((handler, element) => {
        element.removeEventListener('change', handler);
      });
      trackedListeners.variantSelects.clear();
    }    /**
     * Initialize frequency change handlers
     */
    function initFrequencyChangeHandlers() {
      // Clean up any existing listeners first
      cleanupFrequencyListeners();
      
      const freqGroup = document.getElementById('bundle-frequency-group');
      if (!freqGroup) {
        console.warn('Frequency group element not found (#bundle-frequency-group)');
        return;
      }
      
      if (!window.personalised.validation.isValidFrequency()) {
        console.warn('Invalid frequency in bundle state');
        return;
      }
      const freq = window.personalised.bundle.getFrequency();
      const radios = freqGroup.querySelectorAll('input[type="radio"][name="bundle-frequency"]');
      
      if (!radios || radios.length === 0) {
        console.warn('No frequency radio buttons found');
        return;
      }
      
      radios.forEach(radio => {
        if (!radio) return;
        
        // Set initial checked state
        radio.checked = radio.value === String(freq);
        
        // Add change event listener
        radio.addEventListener('change', handleFrequencyChange);
        
        // Track the listener for later cleanup
        trackedListeners.frequency.set(radio, handleFrequencyChange);
      });
    }    /**
     * Initialize remove item handlers
     */
    function initRemoveItemHandlers() {
      // Clean up any existing listeners first
      cleanupRemoveButtonListeners();
      
      const removeButtons = document.querySelectorAll('.bundle-item-remove');
      if (!removeButtons || removeButtons.length === 0) {
        // This is normal when no items are in the bundle
        return;
      }
      
      removeButtons.forEach(button => {
        if (!button) return;
        
        button.addEventListener('click', handleRemoveItem);
        
        // Track the listener for later cleanup
        trackedListeners.removeButtons.set(button, handleRemoveItem);
      });
    }    /**
     * Initialize variant selection handlers
     */
    function initVariantSelectionHandlers() {
      // Clean up any existing listeners first
      cleanupVariantSelectListeners();
      
      const variantSelects = document.querySelectorAll('.personalised-review-select');
      if (!variantSelects || variantSelects.length === 0) {
        // This is normal when no items with variants are in the bundle
        return;
      }
      
      variantSelects.forEach(select => {
        if (!select) return;
        
        select.addEventListener('change', handleVariantChange);
        
        // Track the listener for later cleanup
        trackedListeners.variantSelects.set(select, handleVariantChange);
      });
    }

    /**
     * Initialize sync state event listeners
     */
    function initSyncStateHandlers() {
      // Clean up any existing event bus listeners
      cleanupSyncListeners();
      
      const eventBus = window.personalised.eventBus;
      
      // Show error modal on sync failure (navigation guard handles checkout protection)
      const syncFailedHandler = function() {
        window.personalised.bundleUI.showBundleErrorModal();
      };
      eventBus.on('syncFailed', syncFailedHandler);
      
      // Track event listeners for cleanup
      trackedListeners.syncListeners = [
        { event: 'syncFailed', handler: syncFailedHandler }
      ];
    }
    
    /**
     * Clean up all sync-related event listeners
     */
    function cleanupSyncListeners() {
      const eventBus = window.personalised.eventBus;
      trackedListeners.syncListeners.forEach(listener => {
        eventBus.off(listener.event, listener.handler);
      });
      trackedListeners.syncListeners = [];
    }

    /**
     * Clean up modal-related event listeners
     */
    function cleanupModalListeners() {
      trackedListeners.modalListeners.forEach((handler, element) => {
        element.removeEventListener('click', handler);
      });
      trackedListeners.modalListeners.clear();

      // Clean up any event bus subscriptions for modals
      if (window.personalised.eventBus) {
        window.personalised.eventBus.off('syncFailed', window.personalised.bundleUI?.showBundleErrorModal);
      }
    }

    /**
     * Clean up performance utils listeners
     */
    function cleanupPerformanceListeners() {
      trackedListeners.performanceListeners.forEach((handler, element) => {
        element.removeEventListener('click', handler);
      });
      trackedListeners.performanceListeners.clear();
    }

    /**
     * Clean up submission result event listeners
     */
    function cleanupSubmissionListeners() {
      const eventBus = window.personalised.eventBus;
      trackedListeners.submissionListeners.forEach(listener => {
        eventBus.off(listener.event, listener.handler);
      });
      trackedListeners.submissionListeners = [];
    }

    /**
     * Clean up all event handlers to prevent memory leaks
     */
    function cleanupAllHandlers() {
      cleanupFrequencyListeners();
      cleanupRemoveButtonListeners();
      cleanupVariantSelectListeners();
      cleanupSyncListeners();
      cleanupSubmissionListeners();
      cleanupModalListeners();
      cleanupPerformanceListeners();
    }

    /**
     * Initialize handlers for bundle submission results
     * Only relevant in EDIT mode for Recharge subscription updates
     */
    function initSubmissionResultHandlers() {
      const eventBus = window.personalised.eventBus;
      if (!eventBus) return;

      // Clean up any existing submission listeners first
      cleanupSubmissionListeners();

      // Handle successful bundle updates
      const successHandler = function() {
        const container = document.querySelector('#personalised-message-container');
        if (container) {
          container.innerHTML = '<div class="success-message">Subscription updated successfully.</div>';
          container.classList.add('visible');
          setTimeout(() => container.classList.remove('visible'), 5000);
        }
      };
      eventBus.on('bundleUpdateSuccess', successHandler);

      // Handle bundle update errors
      const errorHandler = function() {
        const container = document.querySelector('#personalised-message-container');
        if (container) {
          container.innerHTML = '<div class="error-message">Something went wrong. Please try again.</div>';
          container.classList.add('visible');
          setTimeout(() => container.classList.remove('visible'), 5000);
        }
      };
      eventBus.on('bundleUpdateError', errorHandler);

      // Track handlers for cleanup
      trackedListeners.submissionListeners = [
        { event: 'bundleUpdateSuccess', handler: successHandler },
        { event: 'bundleUpdateError', handler: errorHandler }
      ];
    }

    /**
     * Initialize all event handlers in one consolidated function
     */
    function initAllHandlers() {
      // Clean up any existing handlers first to prevent duplicates
      cleanupAllHandlers();
      
      // Initialize handlers in a specific order
      initSyncStateHandlers();
      initFrequencyChangeHandlers();
      initRemoveItemHandlers();
      initVariantSelectionHandlers();
      initSubmissionResultHandlers(); // Add submission result handlers
    }

    // Public API
    return {
      initAllHandlers,
      cleanupAllHandlers,
      
      // Individual initialization methods maintained for backward compatibility
      initFrequencyChangeHandlers,
      initRemoveItemHandlers,
      initVariantSelectionHandlers,
      initSyncStateHandlers,

      /**
       * Remove all event listeners added by this module.
       * Call before re-initializing to prevent duplicate listeners.
       */
      teardown: function() {
        cleanupFrequencyListeners();
        cleanupRemoveButtonListeners();
        cleanupVariantSelectListeners();
        cleanupSyncListeners();
        cleanupModalListeners();
        cleanupPerformanceListeners();
      }
    };
  })();
  
  // Assign to single namespace
  window.personalised.eventHandlers = eventHandlersModule;
})();
